/**
 * Edit Relationship Modal
 *
 * PE-775: Modal for editing existing object-level relationships.
 * Limited editing for standard relationships.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import { X, Link2, Lock, AlertCircle, ArrowRight } from 'lucide-react';
import {
  useUpdateRelationship,
  getRelationshipTypeLabel,
  getRelationshipTypeColor,
  type ObjectRelationship
} from '../../../services/queries/objectRelationshipQueries';
import { useToast } from '../../../contexts/ToastContext';

interface EditRelationshipModalProps {
  relationship: ObjectRelationship | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditRelationshipModal: React.FC<EditRelationshipModalProps> = ({
  relationship,
  isOpen,
  onClose
}) => {
  const { success, error: showError } = useToast();
  const updateRelationship = useUpdateRelationship();

  // Form state
  const [fieldLabel, setFieldLabel] = useState('');
  const [description, setDescription] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [cascadeDelete, setCascadeDelete] = useState(true);
  const [shareAccessLevel, setShareAccessLevel] = useState<'ReadOnly' | 'ReadWrite' | 'None'>('ReadWrite');
  const [lookupFilter, setLookupFilter] = useState('');

  // Initialize form when relationship changes
  useEffect(() => {
    if (relationship) {
      setFieldLabel(relationship.fieldLabel);
      setDescription(relationship.description || '');
      setIsRequired(relationship.isRequired);
      setCascadeDelete(relationship.cascadeDelete);
      setShareAccessLevel(relationship.shareAccessLevel);
      setLookupFilter(relationship.lookupFilter || '');
    }
  }, [relationship]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!relationship) return;

    // Validation
    if (!fieldLabel.trim()) {
      showError('Validation Error', 'Field Label is required');
      return;
    }

    try {
      await updateRelationship.mutateAsync({
        id: relationship.id,
        fieldLabel: fieldLabel.trim(),
        description: description.trim() || undefined,
        isRequired: relationship.relationshipType === 'master-detail' ? true : isRequired,
        cascadeDelete: relationship.relationshipType === 'master-detail' ? cascadeDelete : false,
        shareAccessLevel: relationship.relationshipType === 'master-detail' ? shareAccessLevel : 'None',
        lookupFilter: lookupFilter.trim() || undefined
      });
      success('Relationship Updated', `"${fieldLabel}" has been updated.`);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update relationship. Please try again.';
      showError('Error', msg);
    }
  };

  if (!isOpen || !relationship) return null;

  const isStandard = relationship.isStandard;
  const isMasterDetail = relationship.relationshipType === 'master-detail';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Link2 className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Edit Relationship</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {relationship.sourceObjectLabel} → {relationship.targetObjectLabel}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Standard Relationship Warning */}
            {isStandard && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 flex items-start gap-3">
                <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-900 dark:text-amber-100">Standard Relationship</p>
                  <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                    This is a standard system relationship. Some properties cannot be modified.
                  </p>
                </div>
              </div>
            )}

            {/* Read-only Info Section */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-3">
              <h3 className="font-medium text-gray-900 dark:text-gray-100">Relationship Details</h3>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block mb-1">Relationship Type</span>
                  <span className={`inline-flex px-2 py-0.5 text-xs rounded font-medium ${getRelationshipTypeColor(relationship.relationshipType)}`}>
                    {getRelationshipTypeLabel(relationship.relationshipType)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block mb-1">Field Name (API)</span>
                  <span className="font-mono text-gray-900 dark:text-gray-100">{relationship.fieldName}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block mb-1">Source Object</span>
                  <span className="text-gray-900 dark:text-gray-100">{relationship.sourceObjectLabel}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block mb-1">Target Object</span>
                  <span className="text-gray-900 dark:text-gray-100">{relationship.targetObjectLabel}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400 block mb-1">Child Relationship Name</span>
                  <span className="font-mono text-gray-900 dark:text-gray-100">{relationship.childRelationshipName}</span>
                </div>
              </div>
            </div>

            {/* Editable Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Field Label <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fieldLabel}
                onChange={(e) => setFieldLabel(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose of this relationship..."
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Lookup Options */}
            {!isMasterDetail && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isRequired"
                    checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)}
                    disabled={isStandard}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50"
                  />
                  <label
                    htmlFor="isRequired"
                    className={`text-sm ${isStandard ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Required - Always require a value for this lookup
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lookup Filter (Optional)
                  </label>
                  <input
                    type="text"
                    value={lookupFilter}
                    onChange={(e) => setLookupFilter(e.target.value)}
                    placeholder="e.g., Status = 'Active'"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Filter which records appear in the lookup picker
                  </p>
                </div>
              </div>
            )}

            {/* Master-Detail Options */}
            {isMasterDetail && (
              <div className="space-y-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <h3 className="font-medium text-purple-900 dark:text-purple-100">Master-Detail Settings</h3>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="cascadeDelete"
                    checked={cascadeDelete}
                    onChange={(e) => setCascadeDelete(e.target.checked)}
                    disabled={isStandard}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50"
                  />
                  <label
                    htmlFor="cascadeDelete"
                    className={`text-sm ${isStandard ? 'text-gray-400' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    Cascade Delete - Delete child records when parent is deleted
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sharing Access
                  </label>
                  <select
                    value={shareAccessLevel}
                    onChange={(e) => setShareAccessLevel(e.target.value as any)}
                    disabled={isStandard}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50"
                  >
                    <option value="ReadWrite">Read/Write - Child inherits parent's full access</option>
                    <option value="ReadOnly">Read Only - Child inherits parent's read access</option>
                    <option value="None">None - No inherited access</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={updateRelationship.isPending}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {updateRelationship.isPending ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditRelationshipModal;
